import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, PlusCircle, Loader2, AlertTriangle, ExternalLink, MessageSquare, Save, Eye, X, Pencil, Check, Brain, Sparkles, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from '@/hooks/ui/use-toast';
import { supabase } from '@/integrations/supabase/client'; // Import Supabase client
import { useAuth } from '@/context/AuthContext'; // Import useAuth
import { useDashboardTab } from '@/context/DashboardTabContext';
import { TablesInsert, Tables } from '@/integrations/supabase/types'; // Import Supabase types
import ReviewFormBuilderSimple from './ReviewFormBuilderSimple';
import { PreviewContainer } from './PreviewContainerSimple';
import { AIQuestionConfig, DEFAULT_AI_CONFIG, type AIQuestionConfigData } from './AIQuestionConfig';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type QuestionType = 'text' | 'rating' | 'multiple_choice' | 'checkboxes' | 'textarea';

interface QuestionOption {
  id: string;
  value: string;
}

interface Question {
  id: string;
  type: QuestionType;
  text: string;
  required: boolean;
  options?: QuestionOption[];
  placeholder?: string;
  visible_on_high_rating?: boolean; // Show for high ratings (default true)
  visible_on_low_rating?: boolean;  // Show for low ratings (default true)
}

interface EmailTemplate {
  id: string;
  name: string;
  configuration: {
    preset?: string;
    logoUrl?: string;
    logoPosition?: 'left' | 'center' | 'right';
    primaryColor?: string;
    fontFamily?: string;
    headerText?: string;
    mainMessage?: string;
    ctaText?: string;
    ctaLink?: string;
    buttonType?: 'review' | 'coupon' | 'loyalty' | 'custom' | 'none';
    selectedReviewId?: string;
    selectedCouponId?: string;
    selectedLoyaltyId?: string;
    footerText?: string;
    html?: string;
  };
  user_id: string;
  created_at: string;
}

interface UITextConfig {
  // Step 1 - Rating
  ratingTitle: string;
  ratingSubtitle: string;
  ratingCTA: string;
  
  // Step 2 - Email
  rewardTitle: string;
  feedbackTitle: string;
  emailLabel: string;
  emailPlaceholder: string;
  emailPlaceholderHigh: string; // New - high rating email placeholder
  emailPlaceholderLow: string; // New - low rating email placeholder
  rewardSubtitle: string; // New - high rating subtitle
  consentText: string;
  consentTextLow: string; // New - low rating consent text
  continueButtonText: string;
  nextButtonText: string;
  
  // Step 3 - Form
  formTitle: string;
  formSubtitle: string;
  formTitleHigh: string; // New - high rating form title
  formSubtitleHigh: string; // New - high rating form subtitle
  formTitleLow: string; // New - low rating form title
  formSubtitleLow: string; // New - low rating form subtitle
  nameLabel: string;
  namePlaceholder: string;
  submitButtonText: string;
  
  // Step 4 - Success
  successTitle: string;
  successMessageHigh: string;
  successMessageLow: string;
  retryButtonText: string;
}

const DEFAULT_UI_TEXT: UITextConfig = {
  ratingTitle: "Rate your experience",
  ratingSubtitle: "with {businessName}",
  ratingCTA: "Tap a star to continue",
  rewardTitle: "Get Your Reward!",
  feedbackTitle: "Tell us more",
  emailLabel: "Email Address",
  emailPlaceholder: "Enter your email for reward",
  emailPlaceholderHigh: "Enter your email for reward",
  emailPlaceholderLow: "Enter your email address",
  rewardSubtitle: "Thank you for your excellent rating! Enter your email to receive a reward.",
  consentText: "I agree to terms",
  consentTextLow: "I agree to terms",
  continueButtonText: "Continue to Google Reviews",
  nextButtonText: "Next",
  formTitle: "Share Your Feedback",
  formSubtitle: "Help us improve by answering a few questions",
  formTitleHigh: "Share Your Experience",
  formSubtitleHigh: "We'd love to hear more about your excellent experience!",
  formTitleLow: "Share Your Feedback",
  formSubtitleLow: "Help us improve by answering a few questions",
  nameLabel: "Your Name (Optional)",
  namePlaceholder: "Enter your name",
  submitButtonText: "Submit Feedback",
  successTitle: "Thank You!",
  successMessageHigh: "Thank you for your excellent rating! We really appreciate your feedback.",
  successMessageLow: "Your feedback has been submitted successfully. Thank you for helping us improve!",
  retryButtonText: "Try Again"
};

interface ReviewFormBuilderProps {
  businessId: string | null; // Can be null if no business is selected
  existingForm?: any; // Form being edited
  onComplete?: () => void; // Called when form is saved successfully
  onCancel?: () => void; // Called when user cancels editing
}

const ReviewFormBuilder: React.FC<ReviewFormBuilderProps> = ({ businessId, existingForm, onComplete, onCancel }) => {
  const { session } = useAuth(); // Get user session
  const { setBusinessDashboardTab } = useDashboardTab();

  // localStorage key for draft data
  const DRAFT_KEY = `reviewFormDraft_${businessId}_${existingForm?.id || 'new'}`;

  // Load draft from localStorage or use defaults
  const loadDraft = () => {
    try {
      const saved = localStorage.getItem(DRAFT_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch (error) {
      console.error('Error loading draft:', error);
      return null;
    }
  };

  const draft = loadDraft();

  const [formId, setFormId] = useState<string | null>(null); // To store the ID if editing an existing form
  const [formName, setFormName] = useState(draft?.formName || ''); // Internal form name
  const [questions, setQuestions] = useState<Question[]>(draft?.questions || []);
  const [starThreshold, setStarThreshold] = useState(draft?.starThreshold || 4); // New state for star threshold
  const [rewardEnabled, setRewardEnabled] = useState(draft?.rewardEnabled || false); // Legacy state - will be migrated to high rating email
  const [enableStep2, setEnableStep2] = useState(draft?.enableStep2 ?? true); // Legacy - kept for backwards compatibility

  // Separate Step 2 enable states for high and low ratings
  const [enableStep2High, setEnableStep2High] = useState(draft?.enableStep2High ?? true);
  const [enableStep2Low, setEnableStep2Low] = useState(draft?.enableStep2Low ?? true);

  // New separate email collection states
  const [highRatingEmailEnabled, setHighRatingEmailEnabled] = useState(draft?.highRatingEmailEnabled || false); // High rating email collection
  const [highRatingEmailRequired, setHighRatingEmailRequired] = useState(draft?.highRatingEmailRequired || false); // High rating email required
  const [highRatingRewardEnabled, setHighRatingRewardEnabled] = useState(draft?.highRatingRewardEnabled || false); // High rating reward system
  const [lowRatingEmailEnabled, setLowRatingEmailEnabled] = useState(draft?.lowRatingEmailEnabled || false); // Low rating email collection
  const [lowRatingEmailRequired, setLowRatingEmailRequired] = useState(draft?.lowRatingEmailRequired || false); // Low rating email required
  const [lowRatingRewardEnabled, setLowRatingRewardEnabled] = useState(draft?.lowRatingRewardEnabled || false); // Low rating reward system
  const [previewHighRating, setPreviewHighRating] = useState(draft?.previewHighRating ?? true); // Toggle for which rating to preview in step 2
  const [googlePlaceId, setGooglePlaceId] = useState<string | null>(draft?.googlePlaceId || null); // State for Google Place ID
  const [googleReviewUrl, setGoogleReviewUrl] = useState<string>(draft?.googleReviewUrl || ''); // State for Google Reviews URL
  const [isEditingUrl, setIsEditingUrl] = useState(false); // State for URL edit mode
  const [uiTextConfig, setUITextConfig] = useState<UITextConfig>(draft?.uiTextConfig || DEFAULT_UI_TEXT); // UI text configuration
  const [selectedRewardTemplateId, setSelectedRewardTemplateId] = useState<string>(draft?.selectedRewardTemplateId || ''); // Selected email template for HIGH rating rewards
  const [selectedLowRatingRewardTemplateId, setSelectedLowRatingRewardTemplateId] = useState<string>(draft?.selectedLowRatingRewardTemplateId || ''); // Selected email template for LOW rating rewards
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([]); // Available email templates
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('step1');
  const [previewStep, setPreviewStep] = useState(1);
  const [businessName, setBusinessName] = useState<string>('Your Business'); // Business name for preview
  const [showPrivacyPolicyDialog, setShowPrivacyPolicyDialog] = useState(false);

  // AI Mode states
  const [aiModeEnabled, setAiModeEnabled] = useState(draft?.aiModeEnabled || false);
  const [aiConfig, setAiConfig] = useState<AIQuestionConfigData>(draft?.aiConfig || DEFAULT_AI_CONFIG);
  const [aiConfigId, setAiConfigId] = useState<string | null>(draft?.aiConfigId || null);

  // Save draft to localStorage whenever form data changes
  useEffect(() => {
    const draftData = {
      formName,
      questions,
      starThreshold,
      rewardEnabled,
      enableStep2,
      enableStep2High,
      enableStep2Low,
      highRatingEmailEnabled,
      highRatingEmailRequired,
      highRatingRewardEnabled,
      lowRatingEmailEnabled,
      lowRatingEmailRequired,
      lowRatingRewardEnabled,
      previewHighRating,
      googlePlaceId,
      googleReviewUrl,
      uiTextConfig,
      selectedRewardTemplateId,
      selectedLowRatingRewardTemplateId,
      aiModeEnabled,
      aiConfig,
      aiConfigId,
      timestamp: Date.now()
    };

    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(draftData));
    } catch (error) {
      console.error('Error saving draft:', error);
    }
  }, [
    formName, questions, starThreshold, rewardEnabled, enableStep2,
    enableStep2High, enableStep2Low,
    highRatingEmailEnabled, highRatingEmailRequired, highRatingRewardEnabled,
    lowRatingEmailEnabled, lowRatingEmailRequired, lowRatingRewardEnabled,
    previewHighRating, googlePlaceId, googleReviewUrl,
    uiTextConfig, selectedRewardTemplateId, selectedLowRatingRewardTemplateId,
    aiModeEnabled, aiConfig, aiConfigId, DRAFT_KEY
  ]);

  // Clear draft on successful save or cancel
  const clearDraft = () => {
    try {
      localStorage.removeItem(DRAFT_KEY);
    } catch (error) {
      console.error('Error clearing draft:', error);
    }
  };

  // Load existing form data when editing
  useEffect(() => {
    const loadExistingForm = async () => {
      if (existingForm) {
        // Check if there's a draft for this form first
        if (draft) {
          console.log('Loading from draft for existing form:', existingForm.id);
          // Draft takes precedence - restore in-progress work
          setFormId(existingForm.id);
          setFormName(draft.formName || existingForm.title || '');
          setStarThreshold(draft.starThreshold ?? 4);
          setRewardEnabled(draft.rewardEnabled ?? false);
          setEnableStep2(draft.enableStep2 ?? true);
          setEnableStep2High(draft.enableStep2High ?? true);
          setEnableStep2Low(draft.enableStep2Low ?? true);
          setHighRatingEmailEnabled(draft.highRatingEmailEnabled ?? false);
          setHighRatingEmailRequired(draft.highRatingEmailRequired ?? false);
          setHighRatingRewardEnabled(draft.highRatingRewardEnabled ?? false);
          setLowRatingEmailEnabled(draft.lowRatingEmailEnabled ?? false);
          setLowRatingEmailRequired(draft.lowRatingEmailRequired ?? false);
          setLowRatingRewardEnabled(draft.lowRatingRewardEnabled ?? false);
          setPreviewHighRating(draft.previewHighRating ?? true);
          setGooglePlaceId(draft.googlePlaceId || null);
          setGoogleReviewUrl(draft.googleReviewUrl || '');
          setUITextConfig(draft.uiTextConfig || DEFAULT_UI_TEXT);
          setSelectedRewardTemplateId(draft.selectedRewardTemplateId || '');
          setSelectedLowRatingRewardTemplateId(draft.selectedLowRatingRewardTemplateId || '');
          setQuestions(draft.questions || []);
          return; // Don't load from database if draft exists
        }

        try {
          // No draft - load from database
          setFormId(existingForm.id);
          setFormName(existingForm.title || '');

          // Load enable_step_2 settings
          if (existingForm.enable_step_2 !== undefined) {
            setEnableStep2(existingForm.enable_step_2);
          }
          // Load separate high/low enable_step_2 settings (new fields)
          if (existingForm.enable_step_2_high !== undefined) {
            setEnableStep2High(existingForm.enable_step_2_high);
          } else if (existingForm.enable_step_2 !== undefined) {
            // Fallback to legacy enable_step_2 if new fields don't exist
            setEnableStep2High(existingForm.enable_step_2);
          }
          if (existingForm.enable_step_2_low !== undefined) {
            setEnableStep2Low(existingForm.enable_step_2_low);
          } else if (existingForm.enable_step_2 !== undefined) {
            // Fallback to legacy enable_step_2 if new fields don't exist
            setEnableStep2Low(existingForm.enable_step_2);
          }

          // Load reward_enabled setting if available (legacy)
          if (existingForm.reward_enabled !== undefined) {
            setRewardEnabled(existingForm.reward_enabled);
          }

          // Load new email collection and reward settings
          if (existingForm.high_rating_email_enabled !== undefined) {
            setHighRatingEmailEnabled(existingForm.high_rating_email_enabled);
          }
          if (existingForm.high_rating_email_required !== undefined) {
            setHighRatingEmailRequired(existingForm.high_rating_email_required);
          }
          if (existingForm.high_rating_reward_enabled !== undefined) {
            setHighRatingRewardEnabled(existingForm.high_rating_reward_enabled);
          }
          if (existingForm.low_rating_email_enabled !== undefined) {
            setLowRatingEmailEnabled(existingForm.low_rating_email_enabled);
          }
          if (existingForm.low_rating_email_required !== undefined) {
            setLowRatingEmailRequired(existingForm.low_rating_email_required);
          }
          if (existingForm.low_rating_reward_enabled !== undefined) {
            setLowRatingRewardEnabled(existingForm.low_rating_reward_enabled);
          }

          // Load reward email templates if available
          if (existingForm.reward_email_template_id) {
            setSelectedRewardTemplateId(existingForm.reward_email_template_id);
          }
          if (existingForm.low_rating_reward_email_template_id) {
            setSelectedLowRatingRewardTemplateId(existingForm.low_rating_reward_email_template_id);
          }

          // Load UI text configuration if available
          if (existingForm.ui_text_config) {
            setUITextConfig(existingForm.ui_text_config as UITextConfig);
          }

          // Fetch questions for this form
          const { data: questionsData, error: questionsError } = await supabase
            .from('questions')
            .select('*')
            .eq('form_id', existingForm.id)
            .order('order', { ascending: true });

          if (questionsError) throw questionsError;

          // Convert database questions to component format
          const loadedQuestions: Question[] = (questionsData || []).map(q => ({
            id: crypto.randomUUID(), // Generate new UUIDs for component state management
            type: q.type as QuestionType,
            text: q.text,
            required: q.required || false,
            placeholder: q.placeholder || undefined, // Load placeholder text
            visible_on_high_rating: q.visible_on_high_rating !== undefined ? q.visible_on_high_rating : true,
            visible_on_low_rating: q.visible_on_low_rating !== undefined ? q.visible_on_low_rating : true,
            options: q.options ? (Array.isArray(q.options) ? q.options.map((opt: any) => ({
              id: crypto.randomUUID(),
              value: opt.value || opt
            })) : []) : undefined
          }));

          setQuestions(loadedQuestions);
          console.log('Loaded existing form from database with questions:', loadedQuestions);
        } catch (error) {
          console.error("Error loading existing form:", error);
          toast.error("Failed to load form data");
        }
      }
    };

    loadExistingForm();
  }, [existingForm]);

  useEffect(() => {
    const fetchData = async () => {
      if (!businessId) return;
      try {
        const { data, error } = await supabase
          .from('businesses')
          .select('review_redirect_star_threshold, google_place_id, name')
          .eq('id', businessId)
          .single();

        if (error) throw error;
        if (data) {
          if (data.review_redirect_star_threshold !== null) {
            setStarThreshold(data.review_redirect_star_threshold);
          }
          setGooglePlaceId(data.google_place_id);

          // Set business name for preview
          if (data.name) {
            setBusinessName(data.name);
          }

          // Generate Google Reviews URL if we have a place ID
          if (data.google_place_id) {
            const reviewUrl = `https://search.google.com/local/writereview?placeid=${data.google_place_id}`;
            setGoogleReviewUrl(reviewUrl);
          }
        }
      } catch (error) {
        console.error("Error fetching business settings:", error);
      }
    };
    fetchData();
  }, [businessId]);

  // Fetch email templates for reward dropdown
  useEffect(() => {
    const fetchEmailTemplates = async () => {
      if (!session?.user?.id) return;
      
      try {
        const { data, error } = await supabase
          .from('email_templates')
          .select('*')
          .eq('user_id', session.user.id)
          .order('created_at', { ascending: false });

        if (error) throw error;
        
        setEmailTemplates(data || []);
      } catch (error) {
        console.error("Error fetching email templates:", error);
        toast.error("Failed to load email templates");
      }
    };
    
    fetchEmailTemplates();
  }, [session?.user?.id]);

  const handleStarThresholdChange = async (newThreshold: number) => {
    setStarThreshold(newThreshold);
    if (!businessId) return;
    try {
      const { error } = await supabase
        .from('businesses')
        .update({ review_redirect_star_threshold: newThreshold })
        .eq('id', businessId);

      if (error) throw error;
      toast.success("Star threshold updated successfully");
    } catch (error) {
      console.error("Error updating star threshold:", error);
      toast.error("Failed to update star threshold");
    }
  };

  // Legacy function removed - reward logic is now handled in the new high/low rating sections

  const updateUIText = (field: keyof UITextConfig, value: string) => {
    setUITextConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Helper function to replace {businessName} placeholder
  const processUIText = (text: string, businessName: string = "Your Business"): string => {
    return text.replace(/{businessName}/g, businessName);
  };

  // Handle tab change and sync with preview
  const handleTabChange = (tabValue: string) => {
    setActiveTab(tabValue);

    // Check if step 2 is enabled for the current preview rating type
    const step2Enabled = previewHighRating ? enableStep2High : enableStep2Low;
    const emailEnabled = previewHighRating ? highRatingEmailEnabled : lowRatingEmailEnabled;

    const stepMap = {
      'step1': 1,  // Rating step
      'step2': step2Enabled ? 2 : 4,  // Form step (always step 2 when enabled, success if step2 disabled)
      'step3': 4   // Success step
    };
    const step = stepMap[tabValue as keyof typeof stepMap] || 1;
    setPreviewStep(step);
  };

  // Handle preview step change and sync with tabs
  const handlePreviewStepChange = (step: number) => {
    setPreviewStep(step);
    const tabMap = {
      1: 'step1',  // Rating → Step 1 tab
      2: 'step2',  // Email → Step 2 tab (Form)
      3: 'step2',  // Form → Step 2 tab (Form) 
      4: 'step3'   // Success → Step 3 tab
    };
    const tab = tabMap[step as keyof typeof tabMap] || 'step1';
    setActiveTab(tab);
  };

  // Update preview step when enableStep2 or email settings change and we're on step2 tab
  React.useEffect(() => {
    if (activeTab === 'step2') {
      // Check if step 2 is enabled for the current preview rating type
      const step2Enabled = previewHighRating ? enableStep2High : enableStep2Low;
      const emailEnabled = previewHighRating ? highRatingEmailEnabled : lowRatingEmailEnabled;

      const stepMap = {
        'step1': 1,  // Rating step
        'step2': step2Enabled ? 2 : 4,  // Form step (always step 2 when enabled, success if step2 disabled)
        'step3': 4   // Success step
      };
      const step = stepMap['step2'];
      setPreviewStep(step);
    }
  }, [enableStep2High, enableStep2Low, highRatingEmailEnabled, lowRatingEmailEnabled, activeTab, previewHighRating]);


  const addQuestion = (type: QuestionType) => {
    const defaultPlaceholders = {
      text: 'Your answer...',
      textarea: 'Your detailed response...',
      rating: undefined, // Rating doesn't need placeholder
      multiple_choice: undefined, // Multiple choice doesn't need placeholder
      checkboxes: undefined // Checkboxes don't need placeholder
    };

    const newQuestion: Question = {
      id: crypto.randomUUID(),
      type: type,
      text: '',
      required: false,
      placeholder: defaultPlaceholders[type],
      visible_on_high_rating: true, // Default: show on both
      visible_on_low_rating: true,
      ...( (type === 'multiple_choice' || type === 'checkboxes') && { options: [{ id: crypto.randomUUID(), value: '' }] } )
    };
    setQuestions([...questions, newQuestion]);
  };

  const updateQuestion = (id: string, field: keyof Question, value: unknown) => {
    setQuestions(questions.map(q => q.id === id ? { ...q, [field]: value } : q));
  };

  const removeQuestion = (id: string) => {
    setQuestions(questions.filter(q => q.id !== id));
  };

  const addOption = (questionId: string) => {
    setQuestions(questions.map(q => {
      if (q.id === questionId && q.options) {
        return { ...q, options: [...q.options, { id: crypto.randomUUID(), value: '' }] };
      }
      return q;
    }));
  };

  const updateOption = (questionId: string, optionId: string, value: string) => {
     setQuestions(questions.map(q => {
      if (q.id === questionId && q.options) {
        return {
          ...q,
          options: q.options.map(opt => opt.id === optionId ? { ...opt, value } : opt)
        };
      }
      return q;
    }));
  };

   const removeOption = (questionId: string, optionId: string) => {
    setQuestions(questions.map(q => {
      if (q.id === questionId && q.options) {
        // Keep at least one option
        if (q.options.length <= 1) {
          toast.warning("Each choice question must have at least one option.");
          return q;
        }
        return { ...q, options: q.options.filter(opt => opt.id !== optionId) };
      }
      return q;
    }));
  };

  const reorderQuestions = (newQuestions: Question[]) => {
    setQuestions(newQuestions);
  };

  const handleSaveForm = async () => {
    // Clear previous validation errors
    setValidationErrors({});

    const errors: {[key: string]: string} = {};

    if (!businessId) {
      toast.error("Please select a business first.");
      return;
    }

    // Check if business has a privacy policy
    try {
      const { data: policyData, error: policyError } = await supabase
        .from('business_policies')
        .select('id')
        .eq('business_id', businessId)
        .maybeSingle();

      if (policyError && policyError.code !== 'PGRST116') {
        throw policyError;
      }

      if (!policyData) {
        setShowPrivacyPolicyDialog(true);
        return;
      }
    } catch (error) {
      console.error('Error checking business policy:', error);
      toast.error("Failed to verify privacy policy");
      return;
    }
    
    if (!formName.trim()) {
      errors['formName'] = "Please enter a form name.";
    }

    // Validate high rating: if step 2 is enabled and NOT AI mode, must have questions OR email collection
    if (!aiModeEnabled && enableStep2High && questions.length === 0 && !highRatingEmailEnabled) {
      toast.error("High rating: Please add at least one question, enable email collection, or disable Step 2.");
      return;
    }

    // Validate low rating: if step 2 is enabled and NOT AI mode, must have questions OR email collection
    if (!aiModeEnabled && enableStep2Low && questions.length === 0 && !lowRatingEmailEnabled) {
      toast.error("Low rating: Please add at least one question, enable email collection, or disable Step 2.");
      return;
    }

    // Validate AI mode: must have at least one topic enabled
    if (aiModeEnabled) {
      const enabledTopicsCount = Object.values(aiConfig.enabled_topics).filter(t => t.enabled).length;
      if (enabledTopicsCount === 0) {
        toast.error("AI Mode: Please enable at least one question topic.");
        return;
      }
    }
    
    // Check if reward email templates are required
    if (highRatingRewardEnabled && !selectedRewardTemplateId) {
      errors['rewardEmailTemplate'] = "Please select an email template for high rating rewards.";
    }
    if (lowRatingRewardEnabled && !selectedLowRatingRewardTemplateId) {
      errors['lowRatingRewardEmailTemplate'] = "Please select an email template for low rating rewards.";
    }
    
    // If there are validation errors, show them and focus the first error field
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      
      // Focus the first error field
      const firstErrorField = Object.keys(errors)[0];
      const errorElement = document.getElementById(
        firstErrorField === 'formName' ? 'form-name' : 
        firstErrorField === 'rewardEmailTemplate' ? 'reward-email-template-select' :
        firstErrorField
      );
      
      if (errorElement) {
        errorElement.focus();
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      
      // Show toast for the first error
      toast.error(Object.values(errors)[0]);
      return;
    }
    // Basic validation for question text and options (only in non-AI mode)
    if (!aiModeEnabled) {
      for (const q of questions) {
          if (!q.text.trim()) {
              toast.error(`Question ${questions.indexOf(q) + 1} cannot be empty.`);
              return;
          }
          if ((q.type === 'multiple_choice' || q.type === 'checkboxes') && q.options) {
              for (const opt of q.options) {
                  if (!opt.value.trim()) {
                      toast.error(`An option in Question ${questions.indexOf(q) + 1} cannot be empty.`);
                      return;
                  }
              }
          }
      }
    }


    setLoading(true);
    try {
      // Save AI config first if AI mode is enabled
      let savedAiConfigId = aiConfigId;
      if (aiModeEnabled) {
        const { data: aiConfigData, error: aiConfigError } = await supabase
          .from('ai_question_configs')
          .upsert({
            ...(aiConfigId && { id: aiConfigId }),
            business_id: businessId,
            enabled_topics: aiConfig.enabled_topics,
            question_style: aiConfig.question_style,
            consider_staff_on_duty: aiConfig.consider_staff_on_duty,
            consider_time_of_day: aiConfig.consider_time_of_day,
            consider_recent_reviews: aiConfig.consider_recent_reviews,
            recent_reviews_lookback_days: aiConfig.recent_reviews_lookback_days,
          }, {
            onConflict: 'business_id'
          })
          .select('id')
          .single();

        if (aiConfigError) {
          console.error('Error saving AI config:', aiConfigError);
          throw new Error('Failed to save AI configuration');
        }
        savedAiConfigId = aiConfigData?.id || null;
        setAiConfigId(savedAiConfigId);
      }

      // 1. Upsert the review_forms record
      const formUpsertData: any = {
        ...(formId && { id: formId }), // Include ID if updating
        business_id: businessId,
        title: formName.trim(),
        active_status: true, // Default to active, could add UI later
        enable_step_2: enableStep2, // Save enable step 2 setting (legacy - for backwards compatibility)
        enable_step_2_high: enableStep2High, // Separate enable step 2 for high ratings
        enable_step_2_low: enableStep2Low, // Separate enable step 2 for low ratings
        reward_enabled: rewardEnabled, // Save reward enabled setting (legacy)
        high_rating_email_enabled: highRatingEmailEnabled, // High rating email collection
        high_rating_email_required: highRatingEmailRequired, // High rating email required
        high_rating_reward_enabled: highRatingRewardEnabled, // High rating reward system
        low_rating_email_enabled: lowRatingEmailEnabled, // Low rating email collection
        low_rating_email_required: lowRatingEmailRequired, // Low rating email required
        low_rating_reward_enabled: lowRatingRewardEnabled, // Low rating reward system
        reward_email_template_id: selectedRewardTemplateId || null, // High rating reward email template
        low_rating_reward_email_template_id: selectedLowRatingRewardTemplateId || null, // Low rating reward email template
        ui_text_config: uiTextConfig, // Save UI text configuration
        ai_mode_enabled: aiModeEnabled, // AI mode flag
        ai_config_id: aiModeEnabled ? savedAiConfigId : null, // Link to AI config
      };

      const { data: formData, error: formError } = await supabase
        .from('review_forms')
        .upsert(formUpsertData)
        .select()
        .single(); // Expecting one record back

      if (formError) throw formError;
      if (!formData) throw new Error("Failed to save form data.");

      const currentFormId = formData.id;
      setFormId(currentFormId); // Store the ID for subsequent saves

      // 2. Delete existing questions for this form (simple approach)
      // More robust: Fetch existing question IDs, compare, delete removed, update existing, insert new
      const { error: deleteError } = await supabase
        .from('questions')
        .delete()
        .eq('form_id', currentFormId);

      if (deleteError) {
          console.warn("Could not delete old questions, proceeding to insert new ones.", deleteError);
          // Decide if this is a critical error or if we can continue
      }

      // 3. Insert the new questions
      const questionInsertData: TablesInsert<'questions'>[] = questions.map((q, index) => ({
        form_id: currentFormId,
        type: q.type,
        text: q.text.trim(),
         required: q.required,
         order: index + 1, // Set order based on array index
         placeholder: q.placeholder || null, // Save placeholder text
         visible_on_high_rating: q.visible_on_high_rating !== undefined ? q.visible_on_high_rating : true,
         visible_on_low_rating: q.visible_on_low_rating !== undefined ? q.visible_on_low_rating : true,
         // Cast options to 'any' to satisfy Supabase's Json type, as QuestionOption[] is valid JSON
         options: (q.type === 'multiple_choice' || q.type === 'checkboxes') ? (q.options as unknown) : null,
       }));

       const { error: questionError } = await supabase
        .from('questions')
        .insert(questionInsertData);

      if (questionError) throw questionError;

      toast.success("Form structure saved successfully!");

      // Clear draft after successful save
      clearDraft();

      // Call onComplete callback if provided
      if (onComplete) {
        onComplete();
      }

    } catch (error: unknown) {
      console.error("Error saving form:", error);
      toast.error("Failed to save form structure", {
        description: error instanceof Error ? error.message : "Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="space-y-6">
      <div className="lg:pr-96">
        {/* Left Panel - Form */}
        <Card className="flex flex-col" style={{ minHeight: 'calc(100vh - 80px)' }}>
          <CardHeader className="flex-shrink-0">
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  {formName.trim() ? formName : (existingForm ? 'Edit Review Form' : 'Create Review Form')}
                </CardTitle>
                <CardDescription>
                  Design and customize your customer review form
                </CardDescription>
              </div>
              {onCancel && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    clearDraft();
                    onCancel();
                  }}
                  className="h-8 w-8 p-0 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="flex flex-col flex-1">
            <div className="flex-1">
            <Tabs value={activeTab} onValueChange={handleTabChange}>
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="step1">Step 1: Rating</TabsTrigger>
                <TabsTrigger value="step2">Step 2: Form</TabsTrigger>
                <TabsTrigger value="step3">Step 3: Success</TabsTrigger>
              </TabsList>

              <TabsContent value="step1" className="space-y-4 mt-4">
                <div className="space-y-6">
                  <div>
                    <Label htmlFor="form-name">Form Name (Internal)</Label>
                    <Input
                      id="form-name"
                      value={formName}
                      onChange={(e) => {
                        setFormName(e.target.value);
                        // Clear error when user starts typing
                        if (validationErrors.formName) {
                          setValidationErrors(prev => ({...prev, formName: ''}));
                        }
                      }}
                      placeholder="e.g., Main Customer Feedback Form"
                      className={`mt-1 ${validationErrors.formName ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                    />
                    {validationErrors.formName && (
                      <p className="text-xs text-red-600 mt-1">{validationErrors.formName}</p>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">
                      This name is for your reference only and won't be shown to customers.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <Label>Review Redirect Threshold</Label>

                    <RadioGroup
                      value={starThreshold.toString()}
                      onValueChange={(value) => handleStarThresholdChange(parseInt(value))}
                      className="flex items-center space-x-4"
                    >
                      {[1, 2, 3, 4, 5].map((star) => (
                        <div key={star} className="flex items-center space-x-2">
                          <RadioGroupItem 
                            value={star.toString()} 
                            id={`star-${star}`} 
                          />
                          <Label htmlFor={`star-${star}`}>
                            {star} Star{star > 1 && 's'}
                          </Label>
                        </div>
                      ))}
                    </RadioGroup>
                    
                    <p className="text-sm text-muted-foreground">
                      Customers with ratings at or above this threshold will be redirected to Google Reviews.
                    </p>

                    <div className="space-y-2 mt-4">
                      <Label>Google Reviews URL</Label>
                      
                      {googlePlaceId ? (
                        <div className="space-y-2">
                          {isEditingUrl ? (
                            <div className="flex items-center gap-2">
                              <Input
                                value={googleReviewUrl}
                                onChange={(e) => setGoogleReviewUrl(e.target.value)}
                                placeholder="https://search.google.com/local/writereview?placeid=..."
                                className="flex-1"
                              />
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => setIsEditingUrl(false)}
                                className="px-3"
                              >
                                <Check className="h-4 w-4" />
                              </Button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <Input
                                value={googleReviewUrl}
                                readOnly
                                className="flex-1 bg-muted text-muted-foreground cursor-default"
                                placeholder="https://search.google.com/local/writereview?placeid=..."
                              />
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => setIsEditingUrl(true)}
                                className="px-3"
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                          <p className="text-sm text-muted-foreground">
                            This URL is automatically generated from your Google Place ID. You can modify it if needed.
                          </p>
                        </div>
                      ) : (
                        <Alert className="border-amber-200 bg-amber-50 text-amber-800">
                          <AlertTriangle className="h-4 w-4" />
                          <AlertDescription>
                            No Google Place ID found for this business. Add your business location in settings to enable Google Reviews redirect.
                          </AlertDescription>
                        </Alert>
                      )}
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="step2" className="space-y-4 mt-4">
                <div className="space-y-6">
                  {/* AI Mode Toggle */}
                  <div className="space-y-4 p-4 rounded-lg border-2 border-dashed border-primary/20 bg-primary/5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-primary/10">
                          <Brain className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <Label className="text-base font-semibold">AI-Powered Questions</Label>
                          <p className="text-sm text-muted-foreground">
                            Let AI generate personalized questions for each customer
                          </p>
                        </div>
                      </div>
                      <Switch
                        checked={aiModeEnabled}
                        onCheckedChange={setAiModeEnabled}
                      />
                    </div>
                    {aiModeEnabled && (
                      <div className="mt-3 pl-12 space-y-2">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Sparkles className="h-4 w-4 text-primary" />
                          <span>Each customer sees ONE dynamic question based on context</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* AI Mode Configuration */}
                  {aiModeEnabled ? (
                    <AIQuestionConfig
                      businessId={businessId || ''}
                      config={aiConfig}
                      onChange={setAiConfig}
                    />
                  ) : (
                  <>
                  {/* High/Low Rating Toggle - MOVED TO TOP */}
                  <div className="space-y-4">
                    <Label>Configure Response by Rating</Label>
                    <p className="text-sm text-muted-foreground">
                      Customize what customers see based on their rating (high vs low).
                    </p>

                    <Tabs
                      value={previewHighRating ? "high" : "low"}
                      onValueChange={(value) => setPreviewHighRating(value === "high")}
                      className="w-full"
                    >
                      <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="high">High Rating (Above Threshold)</TabsTrigger>
                        <TabsTrigger value="low">Low Rating (Below Threshold)</TabsTrigger>
                      </TabsList>

                        <TabsContent value="high" className="space-y-4 mt-4">
                          <div className="space-y-3">
                            {/* Enable Step 2 for High Ratings */}
                            <div className="flex items-center space-x-2 p-3 bg-muted/50 rounded-md">
                              <Checkbox
                                id="step2-enabled-high"
                                checked={enableStep2High}
                                onCheckedChange={(checked) => {
                                  setEnableStep2High(checked as boolean);
                                  // Keep legacy enableStep2 synced for backwards compatibility
                                  setEnableStep2(checked as boolean || enableStep2Low);
                                  if (checked && questions.length === 0) {
                                    addQuestion('text');
                                  }
                                  if (!checked) {
                                    setHighRatingEmailEnabled(false);
                                    setHighRatingEmailRequired(false);
                                  }
                                }}
                              />
                              <div>
                                <Label htmlFor="step2-enabled-high" className="text-sm font-semibold leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                                  Enable Step 2 for High Ratings
                                </Label>
                                <p className="text-xs text-muted-foreground mt-1">
                                  If disabled, high-rating customers are redirected directly to Google Reviews
                                </p>
                              </div>
                            </div>

                            {enableStep2High && (
                              <>
                                <div className="flex items-center justify-between">
                                  <div className="space-y-0.5">
                                    <Label htmlFor="high-rating-email-enabled">Collect Email</Label>
                                    <p className="text-xs text-muted-foreground">
                                      Ask for customer's email address
                                    </p>
                                  </div>
                                  <Switch
                                    id="high-rating-email-enabled"
                                    checked={highRatingEmailEnabled}
                                    onCheckedChange={(checked) => setHighRatingEmailEnabled(checked as boolean)}
                                  />
                                </div>

                            {highRatingEmailEnabled && (
                              <>
                                <div className="ml-6 flex items-center justify-between">
                                  <div className="space-y-0.5">
                                    <Label htmlFor="high-rating-email-required" className="text-sm">Make email required</Label>
                                    <p className="text-xs text-muted-foreground">
                                      Customer must provide email to continue
                                    </p>
                                  </div>
                                  <Switch
                                    id="high-rating-email-required"
                                    checked={highRatingEmailRequired}
                                    onCheckedChange={(checked) => setHighRatingEmailRequired(checked as boolean)}
                                  />
                                </div>
                              </>
                            )}

                            <div className="flex items-center justify-between">
                              <div className="space-y-0.5">
                                <Label htmlFor="high-rating-reward-enabled" className={!highRatingEmailEnabled ? 'text-muted-foreground' : ''}>Give Reward</Label>
                                <p className="text-xs text-muted-foreground">
                                  Send reward email when submitted
                                </p>
                              </div>
                              <Switch
                                id="high-rating-reward-enabled"
                                checked={highRatingRewardEnabled}
                                disabled={!highRatingEmailEnabled}
                                onCheckedChange={(checked) => {
                                  setHighRatingRewardEnabled(checked as boolean);
                                  // Legacy sync
                                  setRewardEnabled(checked as boolean);
                                }}
                              />
                            </div>

                            {highRatingRewardEnabled && (
                              <div className="space-y-2 ml-6">
                                <Label htmlFor="reward-email-template-select" className="text-sm font-medium">
                                  Choose Reward Email Template
                                </Label>
                                <Select
                                  value={selectedRewardTemplateId}
                                  onValueChange={(value) => {
                                    setSelectedRewardTemplateId(value);
                                    // Clear error when user selects
                                    if (validationErrors.rewardEmailTemplate) {
                                      setValidationErrors(prev => ({...prev, rewardEmailTemplate: ''}));
                                    }
                                  }}
                                >
                                  <SelectTrigger
                                    id="reward-email-template-select"
                                    className={`w-full ${validationErrors.rewardEmailTemplate ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                                  >
                                    <SelectValue placeholder="Select an email template" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {emailTemplates.length === 0 ? (
                                      <SelectItem value="" disabled>
                                        No email templates found
                                      </SelectItem>
                                    ) : (
                                      emailTemplates.map((template) => (
                                        <SelectItem key={template.id} value={template.id}>
                                          <div className="flex items-center justify-between w-full">
                                            <span>{template.name}</span>
                                            {template.configuration?.preset === 'coupon-reward' && (
                                              <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded ml-2">
                                                Recommended
                                              </span>
                                            )}
                                          </div>
                                        </SelectItem>
                                      ))
                                    )}
                                  </SelectContent>
                                </Select>
                                {validationErrors.rewardEmailTemplate && (
                                  <p className="text-xs text-red-600">{validationErrors.rewardEmailTemplate}</p>
                                )}
                                <p className="text-xs text-muted-foreground">
                                  💡 We recommend email templates with coupon offer button type.
                                  <span className="text-blue-600 ml-1">
                                    Use email builder to create a template
                                  </span> if you don't have one.
                                </p>
                              </div>
                            )}

                            <p className="text-sm text-muted-foreground">
                              High-rating customers will be prompted for their email to receive a reward before being redirected to Google Reviews.
                            </p>
                          </>
                          )}
                        </div>
                      </TabsContent>

                        <TabsContent value="low" className="space-y-4 mt-4">
                          <div className="space-y-3">
                            {/* Enable Step 2 for Low Ratings */}
                            <div className="flex items-center space-x-2 p-3 bg-muted/50 rounded-md">
                              <Checkbox
                                id="step2-enabled-low"
                                checked={enableStep2Low}
                                onCheckedChange={(checked) => {
                                  setEnableStep2Low(checked as boolean);
                                  // Keep legacy enableStep2 synced for backwards compatibility
                                  setEnableStep2(enableStep2High || checked as boolean);
                                  if (checked && questions.length === 0) {
                                    addQuestion('text');
                                  }
                                  if (!checked) {
                                    setLowRatingEmailEnabled(false);
                                    setLowRatingEmailRequired(false);
                                  }
                                }}
                              />
                              <div>
                                <Label htmlFor="step2-enabled-low" className="text-sm font-semibold leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                                  Enable Step 2 for Low Ratings
                                </Label>
                                <p className="text-xs text-muted-foreground mt-1">
                                  If disabled, low-rating customers go directly to the thank you page
                                </p>
                              </div>
                            </div>

                            {enableStep2Low && (
                              <>
                                <div className="flex items-center justify-between">
                                  <div className="space-y-0.5">
                                    <Label htmlFor="low-rating-email-enabled">Collect Email</Label>
                                    <p className="text-xs text-muted-foreground">
                                      Ask for customer's email address
                                    </p>
                                  </div>
                                  <Switch
                                    id="low-rating-email-enabled"
                                    checked={lowRatingEmailEnabled}
                                    onCheckedChange={(checked) => setLowRatingEmailEnabled(checked as boolean)}
                                  />
                                </div>

                            {lowRatingEmailEnabled && (
                              <div className="ml-6 flex items-center justify-between">
                                <div className="space-y-0.5">
                                  <Label htmlFor="low-rating-email-required" className="text-sm">Make email required</Label>
                                  <p className="text-xs text-muted-foreground">
                                    Customer must provide email to continue
                                  </p>
                                </div>
                                <Switch
                                  id="low-rating-email-required"
                                  checked={lowRatingEmailRequired}
                                  onCheckedChange={(checked) => setLowRatingEmailRequired(checked as boolean)}
                                />
                              </div>
                            )}

                            <div className="flex items-center justify-between">
                              <div className="space-y-0.5">
                                <Label htmlFor="low-rating-reward-enabled" className={!lowRatingEmailEnabled ? 'text-muted-foreground' : ''}>Give Reward</Label>
                                <p className="text-xs text-muted-foreground">
                                  Send reward email when submitted
                                </p>
                              </div>
                              <Switch
                                id="low-rating-reward-enabled"
                                checked={lowRatingRewardEnabled}
                                disabled={!lowRatingEmailEnabled}
                                onCheckedChange={(checked) => setLowRatingRewardEnabled(checked as boolean)}
                              />
                            </div>

                            {lowRatingRewardEnabled && (
                              <div className="space-y-2 ml-6">
                                <Label htmlFor="low-rating-reward-email-template-select" className="text-sm font-medium">
                                  Choose Reward Email Template
                                </Label>
                                <Select
                                  value={selectedLowRatingRewardTemplateId}
                                  onValueChange={(value) => {
                                    setSelectedLowRatingRewardTemplateId(value);
                                    // Clear error when user selects
                                    if (validationErrors.lowRatingRewardEmailTemplate) {
                                      setValidationErrors(prev => ({...prev, lowRatingRewardEmailTemplate: ''}));
                                    }
                                  }}
                                >
                                  <SelectTrigger
                                    id="low-rating-reward-email-template-select"
                                    className={`w-full ${validationErrors.lowRatingRewardEmailTemplate ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                                  >
                                    <SelectValue placeholder="Select an email template" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {emailTemplates.length === 0 ? (
                                      <SelectItem value="" disabled>
                                        No email templates found
                                      </SelectItem>
                                    ) : (
                                      emailTemplates.map((template) => (
                                        <SelectItem key={template.id} value={template.id}>
                                          <div className="flex items-center justify-between w-full">
                                            <span>{template.name}</span>
                                            {template.configuration?.preset === 'coupon-reward' && (
                                              <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded ml-2">
                                                Recommended
                                              </span>
                                            )}
                                          </div>
                                        </SelectItem>
                                      ))
                                    )}
                                  </SelectContent>
                                </Select>
                                {validationErrors.lowRatingRewardEmailTemplate && (
                                  <p className="text-xs text-red-600">{validationErrors.lowRatingRewardEmailTemplate}</p>
                                )}
                                <p className="text-xs text-muted-foreground">
                                  💡 We recommend email templates with coupon offer button type.
                                  <span className="text-blue-600 ml-1">
                                    Use email builder to create a template
                                  </span> if you don't have one.
                                </p>
                              </div>
                            )}

                            <p className="text-sm text-muted-foreground">
                              Low-rating customers will provide feedback through the custom form. Email collection and rewards are optional.
                            </p>
                          </>
                        )}
                          </div>
                        </TabsContent>
                      </Tabs>
                    </div>

                  {/* Custom Questions Section */}
                  {(enableStep2High || enableStep2Low) && (
                    <div className="space-y-4">
                      <Label>Custom Questions</Label>
                      <p className="text-sm text-muted-foreground">
                        Create custom questions to collect detailed feedback from your customers.
                      </p>

                      <ReviewFormBuilderSimple
                        questions={questions}
                        onAddQuestion={addQuestion}
                        onUpdateQuestion={updateQuestion}
                        onRemoveQuestion={removeQuestion}
                        onAddOption={addOption}
                        onUpdateOption={updateOption}
                        onRemoveOption={removeOption}
                        onReorderQuestions={reorderQuestions}
                      />
                    </div>
                  )}
                  </>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="step3" className="space-y-4 mt-4">
                <div className="space-y-6">
                  <div className="space-y-4">
                    <Label>Analytics & Tracking</Label>
                    {businessId && (
                      <div className="space-y-2">
                        <p className="text-sm text-muted-foreground">
                          Review funnel analytics are automatically tracked for this form and available in the main dashboard.
                        </p>
                        <div className="grid grid-cols-2 gap-4 text-xs">
                          <div className="bg-muted p-3 rounded">
                            <div className="font-medium">Tracked Events</div>
                            <div className="mt-1 space-y-1 text-muted-foreground">
                              <div>• Page loads</div>
                              <div>• Rating selections</div>
                              <div>• Email submissions</div>
                              <div>• Form completions</div>
                            </div>
                          </div>
                          <div className="bg-muted p-3 rounded">
                            <div className="font-medium">Success Metrics</div>
                            <div className="mt-1 space-y-1 text-muted-foreground">
                              <div>• Conversion rates</div>
                              <div>• Google redirects</div>
                              <div>• Reward redemptions</div>
                              <div>• Customer satisfaction</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>
            </Tabs>
            </div>

            <div className="mt-auto pt-6 border-t flex items-center">
              <div className="w-24">
                {activeTab !== 'step1' && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      const prevTab = activeTab === 'step3' ? 'step2' : 'step1';
                      handleTabChange(prevTab);
                    }}
                  >
                    <ChevronLeft className="mr-2 h-4 w-4" />
                    Back
                  </Button>
                )}
              </div>
              <div className="flex-1 flex justify-center gap-3">
                {onCancel && (
                  <Button
                    variant="outline"
                    className="w-40"
                    onClick={() => {
                      clearDraft();
                      onCancel();
                    }}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  className="w-40"
                  onClick={handleSaveForm}
                  disabled={loading || !businessId}
                >
                  {loading ? 'Saving...' : 'Save'}
                  <Save className="ml-2 h-4 w-4" />
                </Button>
              </div>
              <div className="w-24 flex justify-end">
                {activeTab !== 'step3' && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      const nextTab = activeTab === 'step1' ? 'step2' : 'step3';
                      handleTabChange(nextTab);
                    }}
                  >
                    Next
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Right Panel - Live Preview */}
      <div className="lg:fixed lg:top-6 lg:w-80 space-y-3 mt-8 lg:mt-0" style={{right: 'calc(24rem / 2 - 20rem / 2)'}}>
        <PreviewContainer
          questions={questions}
          starThreshold={starThreshold}
          rewardEnabled={rewardEnabled} // Keep for backwards compatibility
          enableStep2={enableStep2} // Keep for backwards compatibility
          enableStep2High={enableStep2High}
          enableStep2Low={enableStep2Low}
          highRatingEmailEnabled={highRatingEmailEnabled}
          highRatingEmailRequired={highRatingEmailRequired}
          highRatingRewardEnabled={highRatingRewardEnabled}
          lowRatingEmailEnabled={lowRatingEmailEnabled}
          lowRatingEmailRequired={lowRatingEmailRequired}
          lowRatingRewardEnabled={lowRatingRewardEnabled}
          previewHighRating={previewHighRating}
          businessName={businessName}
          formId={formId}
          editingEnabled={true}
          onUpdateQuestion={updateQuestion}
          uiTextConfig={uiTextConfig}
          onUpdateUIText={updateUIText}
          previewStep={previewStep}
          onPreviewStepChange={handlePreviewStepChange}
        />
      </div>

      {/* Privacy Policy Navigation Confirmation Dialog */}
      <AlertDialog open={showPrivacyPolicyDialog} onOpenChange={setShowPrivacyPolicyDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Privacy Policy Required</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>
                  You need to add a privacy policy before creating review forms.
                </p>
                <p>
                  Go to the <strong>Settings tab</strong> to add your privacy policy?
                </p>
                <p className="text-sm text-muted-foreground">
                  Your review form progress will be auto-saved.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                // Set a flag in sessionStorage to trigger privacy policy highlighting
                sessionStorage.setItem('highlightPrivacyPolicy', 'true');
                setBusinessDashboardTab('settings');
                setShowPrivacyPolicyDialog(false);
              }}
            >
              Go to Settings
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ReviewFormBuilder;
